import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon, cn } from "@nexus/ui";
import type { PreflightReportDetail } from "../../types/preflight.types";
import { summarizeReport } from "../../lib/report-summary";
import { bandTone, scoreToBand } from "../../lib/score-band";
import { mediaKind } from "../../lib/media-type";
import { AnimatedNumber } from "../AnimatedNumber/AnimatedNumber";

const SCORE_TONE_CLASS: Record<ReturnType<typeof bandTone>, string> = {
  success: "text-success",
  warning: "text-warning",
  error: "text-error",
};

interface MetricProps {
  label: string;
  value: ReactNode;
  valueClassName?: string;
}

function Metric({ label, value, valueClassName }: MetricProps) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <dt className="font-mono text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </dt>
      <dd
        className={cn(
          "text-foreground text-3xl font-semibold tabular-nums leading-none tracking-tight",
          valueClassName,
        )}
      >
        {value}
      </dd>
    </div>
  );
}

const COUNT_TONE_CLASS = {
  success: "text-success",
  warning: "text-warning",
  error: "text-error",
} as const;

function metricTone(
  count: number,
  tone: keyof typeof COUNT_TONE_CLASS,
): string | undefined {
  if (count <= 0) return "text-muted-foreground";
  return COUNT_TONE_CLASS[tone];
}

export interface ReportSummaryMetricsProps {
  report: PreflightReportDetail;
  className?: string;
}

/** Report-level aggregate KPIs (KTD9). */
export function ReportSummaryMetrics({
  report,
  className,
}: ReportSummaryMetricsProps) {
  const { t } = useTranslation();
  const stats = useMemo(() => summarizeReport(report.assets), [report.assets]);

  const creativesAnalyzed = report.scoredCount ?? stats.creativesAnalyzed;
  const passedCount = report.criteriaPassedCount ?? stats.passedCount;
  const partialCount = stats.partialCount;
  const failedCount = report.criteriaFailedCount ?? stats.failedCount;
  const avgScore = stats.avgScore;

  const mediaMix = useMemo(() => {
    let video = 0;
    let image = 0;
    for (const asset of report.assets) {
      const kind = mediaKind(asset.contentType);
      if (kind === "video") video += 1;
      else if (kind === "image") image += 1;
    }
    return { video, image };
  }, [report.assets]);
  const hasMediaMix = mediaMix.video > 0 || mediaMix.image > 0;

  return (
    <div
      className={cn(
        "bg-muted/45 @container flex flex-col gap-3 rounded-lg px-5 py-6 sm:px-8 sm:py-7",
        className,
      )}
    >
      {/* Columns follow the block's own width, not the viewport, so the same header fits
          the full report page and the hub's 720px side peek. */}
      <dl
        className={cn(
          "grid grid-cols-2 gap-x-6 gap-y-6 @lg:grid-cols-3",
          hasMediaMix ? "@3xl:grid-cols-6" : "@3xl:grid-cols-5",
        )}
        aria-label={t("preflight.summary.aggregatesAriaLabel")}
      >
        <Metric
          label={t("preflight.summary.creativesAnalyzed")}
          value={<AnimatedNumber value={creativesAnalyzed} />}
        />
        <Metric
          label={t("preflight.summary.avgScore")}
          valueClassName={
            avgScore != null
              ? SCORE_TONE_CLASS[bandTone(scoreToBand(avgScore))]
              : undefined
          }
          value={
            avgScore != null ? (
              <>
                <AnimatedNumber value={avgScore} />
                <span className="text-muted-foreground text-xl font-medium">
                  {" / 100"}
                </span>
              </>
            ) : (
              t("preflight.dash")
            )
          }
        />
        {hasMediaMix ? (
          <Metric
            label={t("preflight.summary.mediaMix")}
            value={
              <span className="flex items-center gap-4 text-2xl">
                <span
                  className="inline-flex items-center gap-1.5"
                  title={t("preflight.media.video")}
                >
                  <Icon
                    name="clapperboard"
                    size="sm"
                    className="text-muted-foreground"
                    aria-label={t("preflight.media.video")}
                  />
                  <AnimatedNumber value={mediaMix.video} />
                </span>
                <span
                  className="inline-flex items-center gap-1.5"
                  title={t("preflight.media.image")}
                >
                  <Icon
                    name="image"
                    size="sm"
                    className="text-muted-foreground"
                    aria-label={t("preflight.media.image")}
                  />
                  <AnimatedNumber value={mediaMix.image} />
                </span>
              </span>
            }
          />
        ) : null}
        <Metric
          label={t("preflight.summary.criteriaPassed")}
          value={<AnimatedNumber value={passedCount} />}
          valueClassName={metricTone(passedCount, "success")}
        />
        <Metric
          label={t("preflight.summary.criteriaPartial")}
          value={<AnimatedNumber value={partialCount} />}
          valueClassName={metricTone(partialCount, "warning")}
        />
        <Metric
          label={t("preflight.summary.criteriaFailed")}
          value={<AnimatedNumber value={failedCount} />}
          valueClassName={metricTone(failedCount, "error")}
        />
      </dl>

      {stats.couldNotAnalyzeCount > 0 ? (
        <p
          className="text-muted-foreground border-border/40 border-t pt-3 text-sm"
          role="status"
        >
          {t("preflight.summary.couldNotAnalyzeNote", {
            count: stats.couldNotAnalyzeCount,
            analyzed: creativesAnalyzed,
            total: report.assets.length,
          })}
        </p>
      ) : null}
    </div>
  );
}
